import React from 'react'

import { TextClamp } from '../text/TextClamp'
import {
  CHART_SEGMENT_CONTROL_CLEARANCE_X,
  chartSegmentControlClearanceBottom,
} from './ChartSegmentControl'
import { ChartSegmentInnerViewRenderer } from './ChartSegmentInnerViews'
import { CHART_SEGMENT_GEAR_CLEARANCE, CHART_SEGMENT_GEAR_INSET } from './gear-layout'

import type { ChartSegmentInnerView, ChartSegmentTabbedView } from '@digital-twins/shared-types'
import type { ChartSegmentLabels } from './ChartSegmentContext'

/** Top inset. The title and the strip sit beside the control, not below it. */
export const CHART_SEGMENT_TABS_TOP_PX = 8
/** Two clamped lines at 16px/1.2. Declared, so a long title cannot push the figure down. */
export const CHART_SEGMENT_TABS_TITLE_BAND_PX = 40
/** A pill: 12px/1.2 text, 4px of padding either side, 1px of border either side. */
export const CHART_SEGMENT_TABS_PILL_HEIGHT_PX = 26
/**
 * A thin horizontal scrollbar. On macOS the scrollbar overlays and costs nothing, but
 * on Windows and Linux `scrollbar-width: thin` takes this much out of the strip's
 * content box the moment the strip actually scrolls.
 */
export const CHART_SEGMENT_TABS_SCROLLBAR_PX = 8
/**
 * The strip's own band: exactly one row of pills, which scrolls sideways. Fixed on
 * purpose — a strip that wrapped took its second row out of the figure below it, and
 * "the graph should never get smaller because the filters appeared".
 *
 * Sized as pill + scrollbar. At the old 32px (minus a 4px gutter) a scrolling strip
 * left 20px for a 26px pill under `overflow-y: hidden`, which shaved the tops and
 * bottoms off every pill exactly when the strip became scrollable.
 */
export const CHART_SEGMENT_TABS_STRIP_BAND_PX =
  CHART_SEGMENT_TABS_PILL_HEIGHT_PX + CHART_SEGMENT_TABS_SCROLLBAR_PX + 2
export const CHART_SEGMENT_TABS_BAND_GAP_PX = 6
export const CHART_SEGMENT_TABS_BOTTOM_PX = 12
/**
 * The column's left gutter. Every row in the tabs column sits this far in from the
 * panel's edge — title, strip and figure alike. A slide whose own figure is
 * drawn to the panel's full inner width opts out per figure (see
 * `ChartSegmentFigureResult.flushLeft`) rather than the gutter being dropped for all
 * four tabbed slide types.
 */
export const CHART_SEGMENT_TABS_GUTTER_LEFT_PX = 16

/**
 * Gap under the title band.
 *
 * Not a free choice: it is whatever it takes to drop the strip clear of the control's
 * clearance, so the strip can keep its whole width for pills instead of reserving the
 * hovered pill's 132px the way the title band above it does. It is a function of the
 * gear's inset, so a panel that has to push its gear down (see
 * `chartSegmentGearInsetTop`) moves the strip down with it instead of quietly sliding
 * the pills under the control.
 */
export function chartSegmentTabsTitleGap(
  gearInsetTop: number = CHART_SEGMENT_GEAR_INSET,
): number {
  return Math.max(
    CHART_SEGMENT_TABS_BAND_GAP_PX,
    chartSegmentControlClearanceBottom(gearInsetTop) -
      (CHART_SEGMENT_TABS_TOP_PX + CHART_SEGMENT_TABS_TITLE_BAND_PX),
  )
}

/** Top edge of the strip band, in the plot box's coordinates. */
export function chartSegmentTabsStripTop(
  hasTitle: boolean,
  gearInsetTop: number = CHART_SEGMENT_GEAR_INSET,
): number {
  return (
    CHART_SEGMENT_TABS_TOP_PX +
    (hasTitle ? CHART_SEGMENT_TABS_TITLE_BAND_PX + chartSegmentTabsTitleGap(gearInsetTop) : 0)
  )
}

/**
 * How much of the control's region the strip band has to reserve on its right.
 *
 * With a title above it the strip already sits below the control and reserves nothing.
 * With no title — which is what `title={props.title || ''}` produces, an empty string
 * being falsy — the strip IS the top row, beside the control, and has to clear the
 * whole hovered pill just as the title band would have.
 */
export function chartSegmentTabsStripPadRight(
  hasTitle: boolean,
  gearInsetTop: number = CHART_SEGMENT_GEAR_INSET,
): number {
  const intrudes =
    chartSegmentTabsStripTop(hasTitle, gearInsetTop) <
    chartSegmentControlClearanceBottom(gearInsetTop)
  return intrudes ? CHART_SEGMENT_CONTROL_CLEARANCE_X - CHART_SEGMENT_GEAR_CLEARANCE : 0
}

/**
 * Where the figure starts inside the plot box. A function of the bands above it and of
 * the control's inset, never of how many segments the breakdown produced.
 */
export function chartSegmentTabsFigureTop(
  hasTitle: boolean,
  gearInsetTop: number = CHART_SEGMENT_GEAR_INSET,
): number {
  return (
    chartSegmentTabsStripTop(hasTitle, gearInsetTop) +
    CHART_SEGMENT_TABS_STRIP_BAND_PX +
    CHART_SEGMENT_TABS_BAND_GAP_PX
  )
}

/**
 * A slide-drawn figure plus the layout concessions it needs. Returning a bare node is
 * still supported and means "no concessions".
 */
export interface ChartSegmentFigureResult {
  node: React.ReactNode
  /**
   * Suppress the column's left gutter for this figure. For a slide whose chart is
   * drawn at a fixed width that already assumes the panel's whole inner width —
   * RatingLikertBarRow is a hardcoded 1256px, which is the 1320px panel minus the
   * gear's 64px clearance — the gutter is 16px of clipping rather than 16px of
   * breathing room.
   */
  flushLeft?: boolean
}

/**
 * Lets the owning slide draw a segment with its own chart instead of the generic
 * inner view — how the segmented Likert bar keeps the baked figure's height. Return
 * null to fall back to the shared renderer.
 */
export type ChartSegmentFigureRenderer = (
  view: ChartSegmentInnerView,
  displayLabels?: Record<string, string>,
  /** The tab's stamped plot (plots plan D1), for the kinds that draw it. */
  plot?: unknown,
) => React.ReactNode | ChartSegmentFigureResult | null

function normalizeFigure(
  result: React.ReactNode | ChartSegmentFigureResult | null,
): { node: React.ReactNode; flushLeft: boolean } {
  if (
    result != null &&
    typeof result === 'object' &&
    !React.isValidElement(result) &&
    !Array.isArray(result) &&
    'node' in result
  ) {
    const figure = result as ChartSegmentFigureResult
    return { node: figure.node, flushLeft: figure.flushLeft === true }
  }
  return { node: result as React.ReactNode, flushLeft: false }
}

export function ChartSegmentTabs(props: {
  view: ChartSegmentTabbedView
  displayLabels?: Record<string, string>
  title?: React.ReactNode
  renderFigure?: ChartSegmentFigureRenderer
  /**
   * The key to the figure's own colours, drawn opposite the title. A Likert bar is
   * four coloured bands and nothing else, and the un-cut chart explains them in this
   * same row — without this the segmented chart shows the bands with no key at all.
   * It sits inside the clearance the control already reserves, so it costs the
   * figure no height.
   */
  legend?: React.ReactNode
  /**
   * The owning panel's gear inset, as passed to ChartSegmentControl. The tabs chrome
   * is laid out around the control, so the two have to agree; a panel that pushes its
   * gear down has to push this down too. Defaults to the same resting inset the
   * control defaults to.
   */
  gearInsetTop?: number
  /** The strip's chrome, in the viewer's language. */
  labels: ChartSegmentLabels
}) {
  const {
    labels,
    view,
    displayLabels,
    title,
    renderFigure,
    legend,
    gearInsetTop = CHART_SEGMENT_GEAR_INSET,
  } = props
  const [activeId, setActiveId] = React.useState(view.tabs[0]?.id)
  const active = view.tabs.find((tab) => tab.id === activeId) ?? view.tabs[0]
  const tabRefs = React.useRef<Array<HTMLButtonElement | null>>([])
  const tablistRef = React.useRef<HTMLDivElement>(null)
  const idPrefix = React.useId()
  // Whether pills are hidden off either end of the strip. The strip scrolls rather
  // than wraps, and the backend only warns about overflow past 24 segments, so
  // without this a reader has no way to know that four long-labelled segments are
  // three segments plus one they cannot see.
  const [hiddenPills, setHiddenPills] = React.useState({ back: false, forward: false })

  const measureOverflow = React.useCallback(() => {
    const node = tablistRef.current
    if (!node) {
      return
    }
    const maxScroll = node.scrollWidth - node.clientWidth
    const next = {
      back: node.scrollLeft > 1,
      forward: maxScroll > 1 && node.scrollLeft < maxScroll - 1,
    }
    setHiddenPills((current) =>
      current.back === next.back && current.forward === next.forward ? current : next,
    )
  }, [])

  React.useLayoutEffect(() => {
    measureOverflow()
    const node = tablistRef.current
    if (!node || typeof ResizeObserver === 'undefined') {
      return
    }
    const observer = new ResizeObserver(measureOverflow)
    observer.observe(node)
    return () => observer.disconnect()
  }, [measureOverflow, view.tabs])

  function scrollStrip(direction: 1 | -1) {
    const node = tablistRef.current
    if (!node) {
      return
    }
    const step = Math.max(120, Math.round(node.clientWidth * 0.8)) * direction
    if (typeof node.scrollBy === 'function') {
      node.scrollBy({ left: step, behavior: 'smooth' })
    } else {
      node.scrollLeft += step
    }
    measureOverflow()
  }

  React.useEffect(() => {
    if (!view.tabs.some((tab) => tab.id === activeId)) {
      setActiveId(view.tabs[0]?.id)
    }
  }, [view.tabs, activeId])

  function handleTabKeyDown(event: React.KeyboardEvent<HTMLButtonElement>, index: number) {
    let nextIndex: number | null = null
    if (event.key.startsWith('Arrow') || event.key === 'Home' || event.key === 'End') {
      event.stopPropagation()
    }
    if (event.key === 'ArrowRight') {
      nextIndex = (index + 1) % view.tabs.length
    } else if (event.key === 'ArrowLeft') {
      nextIndex = (index - 1 + view.tabs.length) % view.tabs.length
    } else if (event.key === 'Home') {
      nextIndex = 0
    } else if (event.key === 'End') {
      nextIndex = view.tabs.length - 1
    }
    if (nextIndex === null) {
      return
    }
    event.preventDefault()
    const nextTab = view.tabs[nextIndex]
    setActiveId(nextTab.id)
    tabRefs.current[nextIndex]?.focus()
  }

  if (!active) {
    return null
  }

  const activeView = active.view as ChartSegmentInnerView
  const slideFigure = normalizeFigure(renderFigure ? renderFigure(activeView, displayLabels, (active as { plot?: unknown }).plot) : null)
  const hasTitle = Boolean(title)
  const stripPadRight = chartSegmentTabsStripPadRight(hasTitle, gearInsetTop)
  const figureGutterLeft = slideFigure.flushLeft ? 0 : CHART_SEGMENT_TABS_GUTTER_LEFT_PX

  return (
    <div
      data-testid="chart-segment-tabs"
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        // Right pad clears the control's resting box for the whole column; the title
        // band below adds the rest of the hovered pill's width, which is the only row
        // the pill can reach.
        padding: `${CHART_SEGMENT_TABS_TOP_PX}px ${CHART_SEGMENT_GEAR_CLEARANCE}px ${CHART_SEGMENT_TABS_BOTTOM_PX}px 0px`,
        boxSizing: 'border-box',
      }}
    >
      {hasTitle ? (
        <div
          data-testid="chart-segment-tab-title-band"
          style={{
            height: CHART_SEGMENT_TABS_TITLE_BAND_PX,
            marginBottom: chartSegmentTabsTitleGap(gearInsetTop),
            paddingLeft: CHART_SEGMENT_TABS_GUTTER_LEFT_PX,
            paddingRight: CHART_SEGMENT_CONTROL_CLEARANCE_X - CHART_SEGMENT_GEAR_CLEARANCE,
            boxSizing: 'border-box',
            flexGrow: 0,
            flexShrink: 0,
            overflow: 'hidden',
            // Row, so the legend sits opposite the title the way the baked chart
            // draws it. The title yields; the legend does not, because a half-drawn
            // key is worse than a clipped word.
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 16,
          }}
        >
          <TextClamp
            data-testid="chart-segment-title"
            minLines={1}
            maxLines={2}
            lineHeight="1.2"
            style={{
              fontSize: 16,
              fontWeight: 600,
              color: 'var(--slide-text-primary)',
              flex: '1 1 0%',
              minWidth: 0,
            }}
          >
            {title}
          </TextClamp>
          {legend ? (
            <div
              data-testid="chart-segment-tab-legend"
              style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}
            >
              {legend}
            </div>
          ) : null}
        </div>
      ) : null}
      <div
        data-testid="chart-segment-tab-band"
        style={{
          position: 'relative',
          height: CHART_SEGMENT_TABS_STRIP_BAND_PX,
          marginBottom: CHART_SEGMENT_TABS_BAND_GAP_PX,
          paddingLeft: CHART_SEGMENT_TABS_GUTTER_LEFT_PX,
          // Zero once a title band has dropped the strip below the control; the full
          // remainder of the hovered pill when the strip is the top row.
          paddingRight: stripPadRight,
          boxSizing: 'border-box',
          flexGrow: 0,
          flexShrink: 0,
        }}
      >
        <div
          ref={tablistRef}
          data-testid="chart-segment-tablist"
          role="tablist"
          aria-label={labels.tabs}
          onScroll={measureOverflow}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            height: '100%',
            // One row, always. Extra segments scroll sideways inside this band rather
            // than wrapping and eating the figure's height.
            flexWrap: 'nowrap',
            overflowX: 'auto',
            overflowY: 'hidden',
            // No bottom gutter: the band itself is sized as pill + scrollbar, so the
            // scrollbar comes out of the band's budget rather than out of the pills.
            boxSizing: 'border-box',
            scrollbarWidth: 'thin',
          }}
        >
          {view.tabs.map((tab, index) => {
            const selected = tab.id === active.id
            return (
              <button
                key={tab.id}
                ref={(node) => {
                  tabRefs.current[index] = node
                }}
                id={`${idPrefix}-tab-${index}`}
                type="button"
                data-testid="chart-segment-tab"
                data-selected={selected ? 'true' : 'false'}
                role="tab"
                aria-selected={selected}
                aria-controls={`${idPrefix}-panel`}
                tabIndex={selected ? 0 : -1}
                title={tab.label}
                onClick={() => setActiveId(tab.id)}
                onKeyDown={(event) => handleTabKeyDown(event, index)}
                style={{
                  border: selected
                    ? '1px solid var(--slide-chart-1)'
                    : '1px solid var(--slide-border-soft)',
                  background: selected ? 'var(--slide-accent-soft)' : 'var(--slide-bg)',
                  borderRadius: 999,
                  padding: '4px 10px',
                  // Declared, so the band that has to hold it (and a scrollbar) is
                  // budgeted against a real number rather than an estimate.
                  height: CHART_SEGMENT_TABS_PILL_HEIGHT_PX,
                  boxSizing: 'border-box',
                  lineHeight: 1.2,
                  fontSize: 12,
                  color: 'var(--slide-text-primary)',
                  cursor: 'pointer',
                  flexShrink: 0,
                  minWidth: 0,
                  maxWidth: 220,
                }}
              >
                <span
                  style={{
                    display: 'inline-block',
                    maxWidth: 130,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    verticalAlign: 'bottom',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {tab.label}
                </span>
                {/* The base size stays on the pill; the low-base warning does not. */}
                <span style={{ marginLeft: 6, color: 'var(--slide-text-muted)' }}>n={tab.n}</span>
              </button>
            )
          })}
        </div>
        {/*
          The cue that the strip holds more than it shows. The strip scrolls instead
          of wrapping, and on macOS the scrollbar is an overlay that only appears once
          you are already scrolling, so without this the last pills simply are not
          there. Both affordances are decoration over the tablist: they duplicate the
          arrow-key navigation the pills already have, so they stay out of the tab
          order and out of the accessibility tree.
        */}
        {hiddenPills.back ? (
          <ChartSegmentStripOverflowCue
            side="start"
            inset={CHART_SEGMENT_TABS_GUTTER_LEFT_PX}
            onScroll={() => scrollStrip(-1)}
            label={labels.showPreviousSegments}
          />
        ) : null}
        {hiddenPills.forward ? (
          <ChartSegmentStripOverflowCue
            side="end"
            inset={stripPadRight}
            onScroll={() => scrollStrip(1)}
            label={labels.showMoreSegments}
          />
        ) : null}
      </div>
      <div
        id={`${idPrefix}-panel`}
        data-testid="chart-segment-figure-band"
        role="tabpanel"
        aria-labelledby={`${idPrefix}-tab-${view.tabs.indexOf(active)}`}
        tabIndex={0}
        style={{
          // Takes every pixel the bands above leave, and no declared height of its
          // own, so the figure is never the thing that gives way.
          flexGrow: 1,
          flexShrink: 1,
          flexBasis: '0%',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflowX: 'hidden',
          overflowY: 'auto',
          scrollbarWidth: 'thin',
          // The column's gutter, unless this slide draws its own full-width figure.
          paddingLeft: figureGutterLeft,
          boxSizing: 'border-box',
        }}
      >
        <div
          data-testid="chart-segment-figure"
          style={{
            // Full height so an inner view sized in percentages (the quadrant map)
            // still resolves against a real number.
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            // Start at the top so a cut keeps the baked figure's placement. The
            // chips above this band are the only extra chrome; leftover height
            // stays empty rather than stretching or recentring the bars.
            justifyContent: 'flex-start',
          }}
        >
          {slideFigure.node ?? (
            <ChartSegmentInnerViewRenderer view={activeView} displayLabels={displayLabels} plot={(active as { plot?: unknown }).plot} />
          )}
        </div>
      </div>
      {view.overflowNote ? (
        <div data-testid="chart-segment-overflow-note" style={{ marginTop: 6, fontSize: 11, color: 'var(--slide-text-muted)', paddingLeft: CHART_SEGMENT_TABS_GUTTER_LEFT_PX }}>
          {view.overflowNote}
        </div>
      ) : null}
    </div>
  )
}

/**
 * Fade plus a nudge button at one end of the tab strip, shown only while pills are
 * actually hidden that way. Decoration over the tablist rather than part of it: the
 * pills already carry arrow-key navigation, so a second keyboard path here would be a
 * duplicate stop rather than an affordance.
 */
function ChartSegmentStripOverflowCue(props: {
  side: 'start' | 'end'
  /** The band's own padding on this side; the cue sits inside the content area. */
  inset: number
  onScroll: () => void
  /** The nudge button's accessible name. */
  label: string
}) {
  const { side, inset, onScroll, label } = props
  const isEnd = side === 'end'
  const edge = isEnd ? { right: inset } : { left: inset }

  return (
    <>
      <div
        data-testid={`chart-segment-tab-overflow-fade-${side}`}
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          ...edge,
          width: 48,
          pointerEvents: 'none',
          background: `linear-gradient(to ${isEnd ? 'right' : 'left'}, rgba(0, 0, 0, 0), var(--slide-bg, #ffffff))`,
        }}
      />
      <button
        type="button"
        data-testid={`chart-segment-tab-overflow-button-${side}`}
        aria-label={label}
        // Not a tab stop: the pills already carry roving arrow-key navigation, so a
        // keyboard user reaches every segment without this. It stays in the
        // accessibility tree (labelled, not aria-hidden) so it is not a focusable
        // element hidden from assistive technology.
        tabIndex={-1}
        onClick={onScroll}
        style={{
          position: 'absolute',
          top: '50%',
          ...edge,
          transform: 'translateY(-50%)',
          width: 20,
          height: 20,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 0,
          borderRadius: 999,
          border: '1px solid var(--slide-border-soft)',
          background: 'var(--slide-bg, #ffffff)',
          color: 'var(--slide-text-muted)',
          cursor: 'pointer',
        }}
      >
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d={isEnd ? 'M9 5l7 7-7 7' : 'M15 5l-7 7 7 7'}
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
    </>
  )
}
